/**
 * Sortie d'un fichier produit par l'application : enregistrement ou partage.
 *
 * Android a deux chemins bien séparés — `ACTION_CREATE_DOCUMENT` pour
 * enregistrer, `ACTION_SEND` pour partager — et les propose au choix. Le
 * navigateur n'offre le second que sous conditions, d'où la question posée ici
 * plutôt que dans l'appelant : c'est la disponibilité du partage qui décide si
 * le choix a lieu d'être.
 */

/**
 * Ce dont le partage a besoin dans `navigator`.
 *
 * Déclaré en paramètre plutôt que lu depuis la variable globale : les deux
 * refus possibles — méthode absente, fichiers refusés — se vérifient alors sans
 * navigateur.
 */
export interface FileShareTarget {
  readonly canShare?: (data: { files: File[] }) => boolean
  readonly share?: (data: { files: File[]; title?: string }) => Promise<void>
}

/**
 * Issue d'une tentative de partage.
 *
 * `dismissed` n'est pas `failed` : le panneau fermé sans choisir est une
 * décision de l'utilisateur, à laquelle il ne faut pas répondre par un message
 * d'échec ni par un enregistrement qu'il n'a pas demandé.
 */
export type ShareOutcome = 'shared' | 'dismissed' | 'failed'

/**
 * Le système peut-il partager ce fichier ?
 *
 * `canShare` est interrogé **avec le fichier**, jamais sans argument : Firefox
 * de bureau expose bien `share`, mais refuse toute charge contenant des
 * fichiers. Un test sur la seule présence de la méthode ouvrirait un choix
 * dont la moitié est morte.
 */
export function canShareFile(
  target: FileShareTarget | undefined,
  file: File,
): boolean {
  if (
    !target ||
    typeof target.canShare !== 'function' ||
    typeof target.share !== 'function'
  ) {
    return false
  }

  try {
    return target.canShare({ files: [file] })
  } catch {
    // Certains navigateurs lèvent sur un type de fichier qu'ils ne connaissent
    // pas, au lieu de renvoyer `false`. Les deux disent la même chose.
    return false
  }
}

/** Ouvre le panneau de partage du système. */
export async function shareFile(
  target: FileShareTarget | undefined,
  file: File,
  title: string,
): Promise<ShareOutcome> {
  if (!canShareFile(target, file)) return 'failed'

  try {
    await target!.share!({ files: [file], title })
    return 'shared'
  } catch (error) {
    return isDismissal(error) ? 'dismissed' : 'failed'
  }
}

/**
 * Fermeture du panneau sans choisir.
 *
 * Le nom de l'erreur est lu directement, sans passer par `DOMException` :
 * WebKit rejette parfois avec une `Error` ordinaire portant le même nom, et
 * une vérification de type ferait alors passer un abandon pour un échec.
 */
function isDismissal(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    (error as { name: unknown }).name === 'AbortError'
  )
}

/**
 * Enregistre le fichier via le téléchargement du navigateur.
 *
 * Le lien est inséré dans le document le temps du clic : Firefox ignore le clic
 * programmé sur un nœud détaché — le même piège que le champ de sélection de
 * fichiers, et avec la même conséquence, un geste sans effet et sans message.
 */
export function downloadFile(file: File): void {
  const url = URL.createObjectURL(file)

  const link = document.createElement('a')
  link.href = url
  link.download = file.name
  link.hidden = true
  document.body.append(link)
  link.click()
  link.remove()

  // Libéré au bout d'un moment : révoquer tout de suite annule le
  // téléchargement sur certains navigateurs, qui n'ont pas fini de lire.
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}
